import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtNum, fmtAmount } from "../lib/format";
import { toast } from "sonner";
import {
  MagnifyingGlass, X, ArrowClockwise, CircleNotch, CaretUp, CaretDown,
  CloudArrowDown, Table as TableIcon, Columns, ArrowUp, ArrowDown,
  ArrowCounterClockwise, Eye, EyeSlash,
} from "@phosphor-icons/react";

// Column catalogue — order = display order. type drives sort + rendering.
const COLS = [
  { key: "so_id", label: "SO #", type: "text" },
  { key: "item", label: "Item", type: "text", w: 60 },
  { key: "customer_po", label: "Customer PO", type: "text" },
  { key: "sold_to_name", label: "Sold-To", type: "text" },
  { key: "ship_to_name", label: "Ship-To", type: "text" },
  { key: "product", label: "Product", type: "text" },
  { key: "product_name", label: "Description", type: "text" },
  { key: "ordered_qty", label: "Ordered", type: "num" },
  { key: "invoiced_qty", label: "Invoiced", type: "num" },
  { key: "open_qty", label: "Open", type: "num", strong: true },
  { key: "uom", label: "UOM", type: "text", w: 60 },
  { key: "net_amount", label: "Net Amt", type: "money" },
  { key: "currency", label: "Cur", type: "text", w: 60 },
  { key: "fulfill_status", label: "Fulfilment", type: "text" },
  { key: "invoice_status", label: "Invoice", type: "text" },
  { key: "requested_date", label: "Requested", type: "date" },
  { key: "posting_date", label: "Posting", type: "date" },
  { key: "sales_org", label: "Sales Org", type: "text" },
];
const NUMERIC = new Set(["num", "money"]);
const COL_BY_KEY = Object.fromEntries(COLS.map((c) => [c.key, c]));
const DEFAULT_ORDER = COLS.map((c) => c.key);
const LS_KEY = "ooa.columns.v1";
// Load the saved column layout (order + hidden) from localStorage, validated against known columns so
// a stale/renamed key can never break the table. Falls back to all columns in default order.
const loadLayout = () => {
  try {
    const j = JSON.parse(localStorage.getItem(LS_KEY) || "null");
    if (!j || !Array.isArray(j.order)) return { order: DEFAULT_ORDER, hidden: [] };
    const order = j.order.filter((k) => COL_BY_KEY[k]);
    for (const k of DEFAULT_ORDER) if (!order.includes(k)) order.push(k);   // append any new columns
    const hidden = (Array.isArray(j.hidden) ? j.hidden : []).filter((k) => COL_BY_KEY[k]);
    return { order, hidden };
  } catch {
    return { order: DEFAULT_ORDER, hidden: [] };
  }
};
const uniq = (rows, key) => [...new Set(rows.map((r) => r[key]).filter((v) => v != null && v !== ""))].sort();

const statusStyle = (v) => {
  const s = String(v || "").toLowerCase();
  if (s === "finished") return { color: "#0F766E", fontWeight: 600 };
  if (s.includes("not started") || s.includes("not")) return { color: "#B45309", fontWeight: 600 };
  if (s.includes("progress") || s.includes("partial")) return { color: "#1D4ED8", fontWeight: 600 };
  return { color: "var(--omh-text-muted)" };
};

export default function SapOpenOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "open_qty", dir: "desc" });
  const [soldTo, setSoldTo] = useState("");
  const [salesOrg, setSalesOrg] = useState("");
  const [fulfil, setFulfil] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [showCols, setShowCols] = useState(false);
  const [layout, setLayout] = useState(loadLayout);
  const colsRef = useRef(null);

  // Persist the chosen column layout so it survives reloads (per-browser preferred layout).
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(layout)); }, [layout]);
  // Close the column chooser on outside click.
  useEffect(() => {
    if (!showCols) return;
    const onDoc = (e) => { if (colsRef.current && !colsRef.current.contains(e.target)) setShowCols(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showCols]);

  const visibleCols = useMemo(
    () => layout.order.filter((k) => !layout.hidden.includes(k)).map((k) => COL_BY_KEY[k]).filter(Boolean),
    [layout]);

  const toggleHide = (key) => setLayout((l) => {
    const isHidden = l.hidden.includes(key);
    if (!isHidden && l.order.length - l.hidden.length <= 1) { toast.error("At least one column must stay visible"); return l; }
    return { ...l, hidden: isHidden ? l.hidden.filter((x) => x !== key) : [...l.hidden, key] };
  });
  const moveCol = (key, dir) => setLayout((l) => {
    const order = [...l.order];
    const i = order.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return l;
    [order[i], order[j]] = [order[j], order[i]];
    return { ...l, order };
  });
  const resetCols = () => { setLayout({ order: DEFAULT_ORDER, hidden: [] }); toast.success("Columns reset to default"); };

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sap-open-orders${refresh ? "?refresh=true" : ""}`);
      if (!data?.ok) {
        toast.error(`Could not load from SAP: ${data?.error || "unknown error"}`);
        setRows([]); setMeta(data);
      } else {
        setRows(data.rows || []); setMeta(data);
        if (refresh) toast.success(`Refreshed · ${data.count} order lines from SAP`);
      }
    } catch (e) {
      toast.error("Failed to reach the open-order service");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(false); }, []);

  const soldToOpts = useMemo(() => uniq(rows, "sold_to_name"), [rows]);
  const salesOrgOpts = useMemo(() => uniq(rows, "sales_org"), [rows]);
  const fulfilOpts = useMemo(() => uniq(rows, "fulfill_status"), [rows]);

  const toggleSort = (k) =>
    setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));

  const view = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (soldTo && r.sold_to_name !== soldTo) return false;
      if (salesOrg && r.sales_org !== salesOrg) return false;
      if (fulfil && r.fulfill_status !== fulfil) return false;
      if (openOnly && !(r.open_qty > 0)) return false;
      if (q) {
        const hay = visibleCols.map((c) => r[c.key]).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const col = COL_BY_KEY[sort.key];
    const numeric = col && NUMERIC.has(col.type);
    out = [...out].sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (numeric) { av = Number(av) || 0; bv = Number(bv) || 0; return sort.dir === "asc" ? av - bv : bv - av; }
      av = String(av ?? ""); bv = String(bv ?? "");
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return out;
  }, [rows, search, soldTo, salesOrg, fulfil, openOnly, sort, visibleCols]);

  const totals = useMemo(() => {
    let open = 0, ordered = 0;
    view.forEach((r) => { open += Number(r.open_qty) || 0; ordered += Number(r.ordered_qty) || 0; });
    return { open, ordered, lines: view.length };
  }, [view]);

  const exportCsv = () => {
    if (!view.length) { toast.error("Nothing to export for the current filters"); return; }
    const head = visibleCols.map((c) => c.label).join(",");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = view.map((r) => visibleCols.map((c) => esc(r[c.key])).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sap-open-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const anyFilter = search || soldTo || salesOrg || fulfil || openOnly;
  const clearAll = () => { setSearch(""); setSoldTo(""); setSalesOrg(""); setFulfil(""); setOpenOnly(false); };

  const renderCell = (r, c) => {
    const v = r[c.key];
    if (c.type === "money") return <span className="mono">{v ? fmtAmount(v, r.currency) : "—"}</span>;
    if (c.type === "num") return <span className="mono">{v != null ? fmtNum(v) : "—"}</span>;
    if (c.key === "fulfill_status" || c.key === "invoice_status") return <span style={statusStyle(v)}>{v || "—"}</span>;
    return v || "—";
  };

  return (
    <Layout breadcrumb={["Setup", "SAP Open Orders"]}>
      <div className="ooa-wrap" data-testid="sap-open-orders-page">
        <div className="ooa-head">
          <div>
            <h1 className="ooa-title"><TableIcon size={22} weight="duotone" /> SAP Open Order Report</h1>
            <div className="ooa-sub">
              Live from SAP ByD Business Analytics · open quantity = ordered − invoiced
              {meta?.fetched_at && <> · fetched {new Date(meta.fetched_at).toLocaleString()}{meta?.cached ? " (cached)" : ""}</>}
            </div>
          </div>
          <div className="ooa-actions">
            <div className="ooa-colwrap" ref={colsRef}>
              <button data-testid="ooa-columns-btn" className={"ooa-btn" + (showCols ? " active" : "")} onClick={() => setShowCols((s) => !s)}>
                <Columns size={15} weight="bold" /> Columns
              </button>
              {showCols && (
                <div className="ooa-colmenu" data-testid="ooa-columns-menu">
                  <div className="ooa-colmenu-head">
                    <span>Show, hide &amp; reorder columns</span>
                    <button data-testid="ooa-columns-reset" className="ooa-colmenu-reset" onClick={resetCols}>
                      <ArrowCounterClockwise size={13} weight="bold" /> Reset
                    </button>
                  </div>
                  <div className="ooa-colmenu-list">
                    {layout.order.map((k, idx) => {
                      const c = COL_BY_KEY[k];
                      const vis = !layout.hidden.includes(k);
                      return (
                        <div key={k} className="ooa-colrow" data-testid={`ooa-colrow-${k}`}>
                          <button className="ooa-colvis" data-testid={`ooa-colvis-${k}`} onClick={() => toggleHide(k)}
                            title={vis ? "Hide column" : "Show column"}>
                            {vis ? <Eye size={15} weight="bold" /> : <EyeSlash size={15} weight="bold" />}
                          </button>
                          <span className={"ooa-collabel" + (vis ? "" : " off")}>{c.label}</span>
                          <button className="ooa-colmove" data-testid={`ooa-colup-${k}`} disabled={idx === 0}
                            onClick={() => moveCol(k, -1)} title="Move up"><ArrowUp size={13} weight="bold" /></button>
                          <button className="ooa-colmove" data-testid={`ooa-coldown-${k}`} disabled={idx === layout.order.length - 1}
                            onClick={() => moveCol(k, 1)} title="Move down"><ArrowDown size={13} weight="bold" /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button data-testid="ooa-export-btn" className="ooa-btn" onClick={exportCsv} disabled={!view.length}>
              <CloudArrowDown size={15} weight="bold" /> Export CSV
            </button>
            <button data-testid="ooa-refresh-btn" className="ooa-btn primary" onClick={() => load(true)} disabled={loading}>
              {loading ? <CircleNotch size={15} weight="bold" className="spin" /> : <ArrowClockwise size={15} weight="bold" />} Refresh from SAP
            </button>
          </div>
        </div>

        <div className="ooa-toolbar">
          <div className="ooa-search">
            <MagnifyingGlass size={16} weight="bold" />
            <input data-testid="ooa-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Live search — PO, product, customer, status…" />
            {search && <button className="ooa-clear" onClick={() => setSearch("")}><X size={13} weight="bold" /></button>}
          </div>
          <select data-testid="ooa-filter-soldto" className="ooa-select" value={soldTo} onChange={(e) => setSoldTo(e.target.value)}>
            <option value="">All customers</option>
            {soldToOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select data-testid="ooa-filter-salesorg" className="ooa-select" value={salesOrg} onChange={(e) => setSalesOrg(e.target.value)}>
            <option value="">All sales orgs</option>
            {salesOrgOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select data-testid="ooa-filter-fulfil" className="ooa-select" value={fulfil} onChange={(e) => setFulfil(e.target.value)}>
            <option value="">All fulfilment</option>
            {fulfilOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <label className="ooa-check" data-testid="ooa-openonly">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> Open only
          </label>
          {anyFilter && <button data-testid="ooa-clear-filters" className="ooa-btn ghost" onClick={clearAll}><X size={13} weight="bold" /> Clear</button>}
          <div className="ooa-count" data-testid="ooa-count">
            {totals.lines} line{totals.lines === 1 ? "" : "s"} · open {fmtNum(totals.open)}
          </div>
        </div>

        <div className="ooa-tablewrap">
          <table className="ooa-table" data-testid="ooa-table">
            <thead>
              <tr>
                {visibleCols.map((c) => (
                  <th key={c.key} data-testid={`ooa-th-${c.key}`} onClick={() => toggleSort(c.key)}
                    className={NUMERIC.has(c.type) ? "num" : ""} style={c.w ? { width: c.w } : undefined}>
                    <span className="ooa-th-inner">
                      {c.label}
                      {sort.key === c.key && (sort.dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={visibleCols.length} className="ooa-empty"><CircleNotch size={18} weight="bold" className="spin" /> Loading from SAP…</td></tr>
              )}
              {!loading && view.length === 0 && (
                <tr><td colSpan={visibleCols.length} className="ooa-empty">{rows.length ? "No rows match the current filters." : (meta && !meta.ok ? `SAP error: ${meta.error}` : "No open-order data.")}</td></tr>
              )}
              {view.map((r, i) => (
                <tr key={`${r.so_id}-${r.item}-${i}`} data-testid={`ooa-row-${i}`}>
                  {visibleCols.map((c) => (
                    <td key={c.key} className={(NUMERIC.has(c.type) ? "num " : "") + (c.strong && r.open_qty > 0 ? "open-pos" : "")}>
                      {renderCell(r, c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
